import { Key, ShieldAlert, Smartphone, Sparkles, UserCheck, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';

interface GetStartedSheetProperties {
  isOpen: boolean;
  onClose: () => void;
  onOpenDownload?: () => void;
}

const STEPS = [
  {
    icon: Smartphone,
    title: 'Download the App',
    description: 'Download and install the official iVALT app on your mobile device (iOS/Android).',
  },
  {
    icon: UserCheck,
    title: 'Register Your Number',
    description: 'Open the app and register your active mobile phone number.',
  },
  {
    icon: Key,
    title: 'Sign In Here',
    description:
      'Enter the same registered mobile number on the DocuID login screen to authenticate.',
  },
  {
    icon: ShieldAlert,
    title: 'Biometric Verification',
    description: 'Accept the push notification and authenticate with biometrics.',
  },
  {
    icon: Sparkles,
    title: "You're in!",
    description: 'Your identity is verified. You can now securely open, save, and share documents.',
  },
] as const;

const GetStartedSheet: React.FC<GetStartedSheetProperties> = ({
  isOpen,
  onClose,
  onOpenDownload,
}) => {
  const [isRendered, setIsRendered] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsRendered(true);
    } else {
      const timer = setTimeout(() => {
        setIsRendered(false);
      }, 350);
      return () => {
        clearTimeout(timer);
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  if (!isRendered) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 bg-black/40 transition-opacity duration-300 ${isOpen ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
        onClick={onClose}
        role="presentation"
      />

      {/* Sheet */}
      <div
        className={`bg-card border-border fixed right-0 bottom-0 left-0 z-50 max-h-[88vh] overflow-y-auto rounded-t-2xl border-t shadow-2xl transition-transform duration-300 ${isOpen ? 'translate-y-0' : 'translate-y-full'}`}
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="border-border flex items-center justify-between border-b px-5 py-4">
          <div className="flex flex-col gap-0.5">
            <div className="inline-flex items-center gap-1.5">
              <span
                className="size-1.5 animate-pulse rounded-full"
                style={{ background: 'var(--accent)' }}
              />
              <span className="text-muted-foreground text-[10px] font-semibold tracking-wider uppercase">
                Quick start
              </span>
            </div>
            <h3 className="text-foreground m-0 text-[15px] font-bold">How to Get Started</h3>
          </div>
          <button
            className="bg-muted hover:bg-accent/10 text-muted-foreground hover:text-foreground flex h-8 w-8 cursor-pointer items-center justify-center rounded-full border-none transition-all hover:scale-110 active:scale-95"
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <X size={16} />
          </button>
        </div>

        {/* Animated step timeline */}
        <div className="px-5 pt-5 pb-2 select-none">
          <div className="space-y-0">
            {STEPS.map((step, index) => {
              const isLast = index === STEPS.length - 1;
              const StepIcon = step.icon;
              return (
                <div
                  key={step.title}
                  className="relative flex gap-3 pb-5 last:pb-0"
                  style={{
                    animation: isOpen
                      ? `gs-fade-in-up 0.5s cubic-bezier(0.16, 1, 0.3, 1) ${0.08 + index * 0.1}s both`
                      : undefined,
                  }}
                >
                  {/* Connector line */}
                  {!isLast && (
                    <div
                      className="absolute top-9 bottom-0 left-[17px] w-px"
                      style={{
                        background: 'linear-gradient(to bottom, var(--accent), var(--border))',
                      }}
                    />
                  )}

                  {/* Icon circle */}
                  <div className="relative shrink-0">
                    <div
                      className="flex size-[35px] items-center justify-center rounded-full border"
                      style={{
                        borderColor: isLast ? 'var(--accent)' : 'var(--border)',
                        background: isLast
                          ? 'color-mix(in srgb, var(--accent) 12%, transparent)'
                          : 'var(--muted)',
                      }}
                    >
                      <StepIcon
                        className="size-4"
                        style={{ color: isLast ? 'var(--accent)' : 'var(--muted-foreground)' }}
                      />
                    </div>
                  </div>

                  {/* Step content card */}
                  <div className="bg-muted/40 border-border/60 min-w-0 flex-1 rounded-xl border p-3">
                    <div className="mb-1 flex items-center gap-2">
                      <span className="text-muted-foreground/50 font-mono text-[10px] font-semibold tracking-wider uppercase">
                        Step {String(index + 1).padStart(2, '0')}
                      </span>
                      {isLast && (
                        <span
                          className="rounded-full px-2 py-0.5 text-[9px] font-bold tracking-wider uppercase"
                          style={{ background: 'var(--accent)', color: '#fff' }}
                        >
                          Done
                        </span>
                      )}
                    </div>
                    <p className="text-foreground m-0 text-sm leading-snug font-semibold">
                      {step.title}
                    </p>
                    <p className="text-muted-foreground m-0 mt-0.5 text-xs leading-relaxed">
                      {step.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* CTA */}
        {onOpenDownload && (
          <div className="px-5 pt-4 pb-2">
            <button
              onClick={() => {
                onClose();
                onOpenDownload();
              }}
              className="flex w-full cursor-pointer items-center justify-center gap-2 rounded-xl border-none py-3 text-sm font-semibold text-white transition-all hover:opacity-90 active:scale-[0.98]"
              style={{ background: 'var(--accent)' }}
              type="button"
            >
              <Smartphone size={15} />
              Download the iVALT App
            </button>
          </div>
        )}

        {/* Footer note */}
        <div className="px-5 pt-3 pb-5">
          <p className="text-muted-foreground m-0 text-center text-[10px]">
            Make sure notifications are enabled for the iVALT mobile app on your phone.
          </p>
        </div>
      </div>

      {/* Keyframe — mirrors dashboard fade-in-up */}
      <style>{`
        @keyframes gs-fade-in-up {
          from { opacity: 0; transform: translateY(14px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </>
  );
};

export default GetStartedSheet;
